import {
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * D4. Wniosek o wydanie abonamentu mieszkańca (karty opłaty zryczałtowanej) w strefie płatnego parkowania — art. 13b
 * ust. 3–4 ustawy o drogach publicznych; rodzaje abonamentów, stawki i dokumenty określa uchwała rady gminy i regulamin
 * strefy. Brak wzoru ogólnopolskiego; układ jak wniosek Gdańskiego Zarządu Dróg „Wniosek o wydanie karty opłaty
 * zryczałtowanej / abonamentu” (2026, https://download.cloudgdansk.pl/gzd-pl/d/2026037631/fp_wniosek-o-wydanie-karty-oplaty-zryczaltowanej_abonamenu_2026-2.docx
 * — pobrany 2026-10-22): rodzaj karty, dane pojazdu i okres, wykaz dokumentów dla mieszkańca zameldowanego i dla
 * właściciela lokalu bez zameldowania. Formularz ogólny — lista załączników jest lokalna (uchwała), do potwierdzenia
 * z zarządem drogi; czynność materialno-techniczna, nie decyzja.
 */
const NOT_REGISTERED: EbiCondition = { field: "podstawaZamieszkania", equals: "wlascicielLokalu" };
const NOT_OWNER: EbiCondition = { field: "tytulDoPojazdu", equals: "umowa" };

const D4: IEbiFormDefinition = {
  code: "D4",
  version: 1,
  title: "Wniosek o wydanie abonamentu mieszkańca w strefie płatnego parkowania",
  category: EEbiFormCategory.Roads,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Abonament (karta opłaty zryczałtowanej) mieszkańca na postój pojazdu w strefie płatnego parkowania.",
  keywords: ["abonament", "parkowanie", "strefa płatnego parkowania", "SPP", "karta mieszkańca", "opłata zryczałtowana"],
  organ: "Zarząd drogi prowadzący strefę płatnego parkowania (np. zarząd dróg miejskich)",
  legalBasis:
    "art. 13b ust. 3–4 ustawy z dnia 21 marca 1985 r. o drogach publicznych; uchwała rady gminy w sprawie strefy płatnego parkowania i regulamin strefy",
  request:
    "Wnoszę o wydanie abonamentu mieszkańca (karty opłaty zryczałtowanej) uprawniającego do postoju w strefie płatnego parkowania pojazdu wskazanego we wniosku.",
  feeLabel: "wg stawek",
  fee: "Opłata abonamentowa według uchwały rady gminy (stawki i okresy są lokalne); bez opłaty skarbowej. Pełnomocnictwo — 17 zł.",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "abonament",
      title: "Abonament",
      description:
        "Rodzaje abonamentów, stawki i wymagane dokumenty określa uchwała rady gminy i regulamin strefy płatnego parkowania — urząd może poprosić o dodatkowe dokumenty.",
      fields: [
        {
          code: "rodzajAbonamentu",
          type: "text",
          label: "Rodzaj abonamentu (karty) według uchwały rady gminy",
          required: true,
          maxLength: 200,
          placeholder: "np. karta opłaty zryczałtowanej mieszkańca — roczna",
        },
        {
          code: "strefa",
          type: "text",
          label: "Strefa, rejon lub obszar strefy płatnego parkowania",
          hint: "Jeśli uchwała dzieli strefę na rejony lub obszary.",
          maxLength: 200,
        },
        { code: "okresOd", type: "date", label: "Abonament ma obowiązywać od", required: true, width: "half" },
        { code: "okresDo", type: "date", label: "do (jeśli na określony czas)", width: "half" },
      ],
    },
    {
      code: "zamieszkanie",
      title: "Zamieszkanie w strefie",
      description: "Adres zamieszkania podany w danych wnioskodawcy musi leżeć w strefie płatnego parkowania.",
      fields: [
        {
          code: "podstawaZamieszkania",
          type: "radio",
          label: "Zamieszkuję w strefie płatnego parkowania",
          required: true,
          defaultValue: "zameldowanie",
          options: [
            { value: "zameldowanie", label: "jako osoba zameldowana na pobyt stały lub czasowy — urząd sprawdzi zameldowanie w rejestrze mieszkańców" },
            { value: "wlascicielLokalu", label: "bez zameldowania — jako właściciel lokalu mieszkalnego, w którym mieszkam" },
          ],
        },
        {
          code: "kwLokalu",
          type: "text",
          label: "Numer księgi wieczystej lokalu",
          required: true,
          visibleWhen: NOT_REGISTERED,
          maxLength: 30,
          placeholder: "np. GD1G/00012345/6",
        },
        {
          code: "oswiadczenieZamieszkanie",
          type: "declaration",
          label: "Oświadczam, że zamieszkuję w lokalu mieszkalnym położonym w strefie płatnego parkowania, którego jestem właścicielem.",
          required: true,
          visibleWhen: NOT_REGISTERED,
        },
      ],
    },
    {
      code: "pojazd",
      title: "Pojazd",
      fields: [
        { code: "nrRejestracyjny", type: "text", label: "Numer rejestracyjny", required: true, width: "half", maxLength: 15, placeholder: "np. GD 12345" },
        { code: "marka", type: "text", label: "Marka", required: true, width: "half", maxLength: 100 },
        {
          code: "tytulDoPojazdu",
          type: "radio",
          label: "Pojazd",
          required: true,
          defaultValue: "wlasciciel",
          options: [
            { value: "wlasciciel", label: "jest moją własnością (współwłasnością) — wynika to z dowodu rejestracyjnego" },
            {
              value: "umowa",
              label: "używam na podstawie umowy leasingu, kredytu, najmu długoterminowego, użyczenia albo umowy z pracodawcą (samochód służbowy)",
            },
          ],
        },
        feeInfo(
          "infoOplata",
          "Opłatę abonamentową wnosi się według uchwały rady gminy — jeśli zapłaciłaś/zapłaciłeś przelewem, dołącz potwierdzenie; w przeciwnym razie urząd poda kwotę i sposób zapłaty.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    { code: "dowodRejestracyjny", label: "Dowód rejestracyjny pojazdu", hint: "Skan obu stron.", required: true },
    {
      code: "umowaPojazd",
      label: "Umowa leasingu, kredytu, najmu długoterminowego, użyczenia albo umowa z pracodawcą o używaniu samochodu służbowego",
      hint: "Z danymi pojazdu (numer rejestracyjny lub VIN). Niektóre gminy wymagają umowy użyczenia poświadczonej notarialnie.",
      visibleWhen: NOT_OWNER,
      required: true,
    },
    {
      code: "ksiegaWieczysta",
      label: "Wydruk treści księgi wieczystej lokalu mieszkalnego",
      hint: "Z centralnej bazy danych ksiąg wieczystych.",
      visibleWhen: NOT_REGISTERED,
      required: true,
    },
    {
      code: "rozliczeniePit",
      label: "Dowód rozliczania podatku dochodowego w gminie",
      hint: "Jeśli wymaga go uchwała rady gminy (np. Warszawa, Kraków): pierwsza strona zeznania PIT za poprzedni rok z UPO albo zaświadczenie urzędu skarbowego, albo ZAP-3.",
    },
    feeProofAttachment("Dowód wniesienia opłaty abonamentowej", {
      required: false,
      hint: "Jeśli opłata została już wniesiona przelewem — inaczej urząd poda kwotę i sposób zapłaty.",
    }),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default D4;
